import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { CheckCheck, Clock, AlertCircle, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import { isSalesManager } from './accessControl.js';

const SalesOrderApprovalList = ({ db, user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    return Number(sessionStorage.getItem('salesOrderApproval_itemsPerPage')) || 10;
  });
  const [currentPage, setCurrentPage] = useState(() => {
    return Number(sessionStorage.getItem('salesOrderApproval_currentPage')) || 1;
  });
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState(() => {
    const savedFilters = sessionStorage.getItem('salesOrderApproval_filters');
    return savedFilters ? JSON.parse(savedFilters) : {
      startDate: '',
      endDate: '',
      customerName: '',
      salesPerson: '',
      orderType: '',
      status: ''
    };
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    sessionStorage.setItem('salesOrderApproval_filters', JSON.stringify(filters));
    sessionStorage.setItem('salesOrderApproval_currentPage', currentPage.toString());
    sessionStorage.setItem('salesOrderApproval_itemsPerPage', itemsPerPage.toString());
  }, [filters, currentPage, itemsPerPage]);

  useEffect(() => {
    const approvalsQuery = query(
      collection(db, 'orders'),
      where('status', '==', 'pending_approval')
    );

    const unsubscribe = onSnapshot(
      approvalsQuery,
      (snapshot) => {
        const pendingOrders = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
            const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
            return bTime - aTime;
          });

        setOrders(pendingOrders);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading sales order approvals:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db]);

  const isMobile = windowWidth <= 768;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const filteredAndSortedOrders = useMemo(() => {
    let filterableOrders = [...orders];

    filterableOrders = filterableOrders.filter((order) => {
      if (filters.startDate || filters.endDate) {
        const orderDate = new Date(order.date || order.createdAt);
        orderDate.setHours(0, 0, 0, 0);

        if (filters.startDate) {
          const start = new Date(filters.startDate);
          start.setHours(0, 0, 0, 0);
          if (orderDate < start) return false;
        }

        if (filters.endDate) {
          const end = new Date(filters.endDate);
          end.setHours(0, 0, 0, 0);
          if (orderDate > end) return false;
        }
      }

      if (filters.customerName && !order.customerName?.toLowerCase().includes(filters.customerName.toLowerCase())) return false;
      if (filters.salesPerson && !order.salesPerson?.toLowerCase().includes(filters.salesPerson.toLowerCase())) return false;
      if (filters.orderType && order.orderType?.toLowerCase() !== filters.orderType.toLowerCase()) return false;
      if (filters.status && order.status?.toLowerCase() !== filters.status.toLowerCase()) return false;

      return true;
    });

    if (sortColumn) {
      filterableOrders.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        if (sortColumn === 'createdAt' || sortColumn === 'date') {
          const dateA = new Date(aValue || a.createdAt || 0);
          const dateB = new Date(bValue || b.createdAt || 0);
          return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        }

        if (sortColumn === 'orderValue') {
          const amountA = Number(aValue || 0);
          const amountB = Number(bValue || 0);
          return sortDirection === 'asc' ? amountA - amountB : amountB - amountA;
        }

        const safeA = String(aValue || '').toLowerCase();
        const safeB = String(bValue || '').toLowerCase();
        return sortDirection === 'asc' ? safeA.localeCompare(safeB) : safeB.localeCompare(safeA);
      });
    }

    return filterableOrders;
  }, [filters, orders, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedOrders.length / itemsPerPage));
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedOrders.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, filteredAndSortedOrders, itemsPerPage]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' ^' : ' v';
  };

  const formatDate = (value) => {
    if (!value) return '---';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '---';
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getActionConfig = (order) => {
    if (isSalesManager(user) && order.status === 'pending_approval') {
      return {
        label: 'Review',
        style: reviewActionBtnStyle,
        icon: null,
        onClick: () => navigate(`/orders/${order.id}`)
      };
    }

    return {
      label: 'Preview',
      style: previewActionBtnStyle,
      icon: <Eye size={14} />,
      onClick: () => navigate(`/orders/${order.id}`)
    };
  };

  const renderStatus = (status) => {
    switch (status) {
      case 'pending_approval':
        return <span style={{ color: '#2563eb', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Waiting Approval</span>;
      case 'rejected':
        return <span style={{ color: '#c2410c', display: 'flex', alignItems: 'center', gap: '5px' }}><AlertCircle size={14} /> Rejected</span>;
      default:
        return status ? status.replace(/_/g, ' ') : '---';
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <h2 style={{ marginBottom: '20px', fontSize: isMobile ? '20px' : '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <CheckCheck size={24} color="#2563eb" /> Order Approval ({filteredAndSortedOrders.length})
      </h2>

      <div style={{ ...filterSectionStyle, gap: isMobile ? '10px' : '15px', padding: isMobile ? '12px' : '15px' }}>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>From Date</label>
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>To Date</label>
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Customer</label>
          <input type="text" name="customerName" value={filters.customerName} onChange={handleFilterChange} placeholder="Search Customer" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Salesperson</label>
          <input type="text" name="salesPerson" value={filters.salesPerson} onChange={handleFilterChange} placeholder="Search Salesperson" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Order Type</label>
          <select name="orderType" value={filters.orderType} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            <option value="Sales">Sales</option>
            <option value="Rental">Rental</option>
          </select>
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Status</label>
          <select name="status" value={filters.status} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            <option value="pending_approval">Waiting Approval</option>
          </select>
        </div>
      </div>

      {orders.length === 0 ? (
        <p style={{ padding: '30px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
          No orders are waiting for Sales Manager approval.
        </p>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '900px' : 'auto' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle} onClick={() => handleSort('createdAt')}>Date{getSortIcon('createdAt')}</th>
                <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => handleSort('customerName')}>Customer{getSortIcon('customerName')}</th>
                <th style={thStyle} onClick={() => handleSort('orderNumber')}>Order Number{getSortIcon('orderNumber')}</th>
                <th style={thStyle} onClick={() => handleSort('salesPerson')}>Salesperson{getSortIcon('salesPerson')}</th>
                <th style={thStyle} onClick={() => handleSort('orderType')}>Type{getSortIcon('orderType')}</th>
                <th style={thStyle} onClick={() => handleSort('orderValue')}>Value{getSortIcon('orderValue')}</th>
                <th style={thStyle} onClick={() => handleSort('status')}>Status{getSortIcon('status')}</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    No orders match the current filters.
                  </td>
                </tr>
              ) : (
                paginatedOrders.map((order) => {
                  const actionConfig = getActionConfig(order);
                  const subjectPreview = order.subject
                    ? `${order.subject.substring(0, 40)}${order.subject.length > 40 ? '...' : ''}`
                    : 'No Subject';

                  return (
                    <tr
                      key={order.id}
                      style={{ borderBottom: '1px solid #e5e7eb' }}
                      className="clickable-row"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <td style={tdStyle}>{formatDate(order.date || order.createdAt)}</td>
                      <td style={{ ...tdStyle, textAlign: 'left' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{order.customerName || '---'}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{subjectPreview}</div>
                      </td>
                      <td style={tdStyle}>
                        <strong style={{ fontSize: '13px' }}>{order.orderNumber || order.id.substring(0, 8).toUpperCase()}</strong>
                      </td>
                      <td style={tdStyle}>{order.salesPerson || 'N/A'}</td>
                      <td style={tdStyle}>{order.orderType || '---'}</td>
                      <td style={tdStyle}>{order.orderValue ? `Rs ${Number(order.orderValue).toLocaleString('en-IN')}` : 'Rs 0'}</td>
                      <td style={tdStyle}>{renderStatus(order.status)}</td>
                      <td style={tdStyle}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            actionConfig.onClick();
                          }}
                          style={actionConfig.style}
                        >
                          {actionConfig.icon}
                          {actionConfig.label}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            padding: '15px',
            borderTop: '1px solid #e5e7eb',
            gap: isMobile ? '15px' : '0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: '10px', fontSize: '13px', color: '#374151' }}>Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ padding: '5px 8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={paginationButtonStyle}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#374151' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={paginationButtonStyle}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '12px 15px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', cursor: 'pointer' };
const tdStyle = { padding: '10px 15px', fontSize: '13px', textAlign: 'center' };
const paginationButtonStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '5px', backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: '13px' };
const previewActionBtnStyle = { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '8px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' };
const reviewActionBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px' };
const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', minWidth: '150px', flex: '1' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const filterInputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };

export default SalesOrderApprovalList;
